import React, {useEffect, useRef, useState} from 'react';

import {colors} from 'js-libs/sweetcommons/styles';
import debounce from 'lodash/debounce';
import styled from 'styled-components';

interface Props {
  delay?: number;
  initialValue?: string;
  value?: string;
  onChange: (phrase: string) => Promise<unknown> | unknown;
  containerClassName?: string;
  className?: string;
  autoFocus?: boolean;
  placeholder?: string;
  id?: string;
  isSearching?: boolean;
}

export function SearchInputField({
  delay = 1000,
  initialValue,
  value,
  onChange,
  containerClassName = '',
  className = '',
  isSearching = false,
  ...other
}: Props): JSX.Element {
  const [content, setContent] = useState(
    initialValue != null ? initialValue : value != null ? value : ''
  );
  const [searching, setSearching] = useState(false);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    // keep it always fresh to be referenced by handleDelayedChange
    onChangeRef.current = onChange;
  }, [onChange]);

  function handleDelayedChange(content: string) {
    const changeResult = onChangeRef.current(content);

    if (isPromise(changeResult)) {
      setSearching(true);
      changeResult.then(
        () => setSearching(false),
        (e) => {
          setSearching(false);
          throw e;
        }
      );
    }
  }

  const delayedOnChange = React.useCallback(
    debounce(handleDelayedChange, delay),
    []
  );

  useEffect(() => {
    if (value != null) {
      setContent(value);
    }
  }, [value]);

  function handleKeyPress(e: React.KeyboardEvent<HTMLInputElement>) {
    const {key} = e;
    // we need wait for onChange handler to run
    // so the content is updated and the delayed serach can be canceled
    setTimeout(() => {
      if (key === 'Enter') {
        delayedOnChange.cancel();
        handleDelayedChange(content);
      }
    }, 0);
  }

  function updateContent(content: string) {
    setContent(content);
    delayedOnChange(content);
  }

  return (
    <StyledDiv className={containerClassName}>
      <span className="icon-container search">
        <i
          className={`fa ${
            searching || isSearching ? 'fa-circle-notch fa-spin' : 'fa-search'
          }`}
        />
      </span>
      <input
        className={`form-control ${content !== '' ? 'pr-4' : ''} ${className}`}
        type="search"
        value={content}
        onChange={(e) => updateContent(e.target.value)}
        onKeyPress={handleKeyPress}
        {...other}
      />
      {content !== '' ? (
        <span className="icon-container clear">
          <i
            className="far fa-times-circle"
            onClick={() => updateContent('')}
          />
        </span>
      ) : null}
    </StyledDiv>
  );
}

const StyledDiv = styled.div`
  position: relative;

  > .icon-container {
    display: flex;
    align-items: center;
    position: absolute;
    top: 0;
    bottom: 0;
    z-index: 1;

    > i.fa {
      margin: 0 auto;
      color: ${colors.mediumGray};
    }
  }

  > .search {
    left: 0;
    width: 2em;
  }

  > .clear {
    right: 0;
    padding-right: 0.4rem;
  }

  > input {
    padding-left: 2em;
  }

  input[type='search']::-webkit-search-cancel-button {
    display: none;
  }
`;

function isPromise(v: unknown): v is Promise<unknown> {
  return (
    typeof v === 'object' &&
    v != null &&
    'then' in v &&
    // @ts-expect-error we know then is in v
    typeof v?.then === 'function'
  );
}
